import { useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const URGENCY = ["24/7 Emergency", "Same-day", "Standard", "Preventative Maintenance"];

export default function ServiceRequestForm() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    site_address: "",
    urgency: URGENCY[2],
    equipment: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const h = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email, and details.");
      return;
    }
    try {
      setLoading(true);
      const { data } = await axios.post(`${API}/service-request`, form);
      if (data && data.id) {
        toast.success("Received. A service dispatcher will reach out.");
        setForm({
          name: "", company: "", email: "", phone: "",
          site_address: "", urgency: URGENCY[2], equipment: "", message: "",
        });
      } else {
        toast.error("Unexpected response. Please try again.");
      }
    } catch (err) {
      toast.error("Something went wrong. Call us at 631.643.3433.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid="service-request-form"
      className="border-b border-white/10 px-4 md:px-6 py-14 md:py-20"
    >
      <Toaster position="bottom-right" theme="dark" />
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-10 md:col-start-2">
          <h2 className="font-display font-bold text-4xl md:text-6xl leading-[0.95] uppercase mb-3">
            Request Service.
          </h2>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-white/50 mb-10">
            24/7 Dispatch · Response within one hour for emergencies
          </p>

          <form onSubmit={submit} className="space-y-8">
            <div className="grid grid-cols-2 gap-6">
              <Field label="Name" testid="svc-name" value={form.name} onChange={h("name")} required />
              <Field label="Company" testid="svc-company" value={form.company} onChange={h("company")} />
            </div>
            <div className="grid grid-cols-2 gap-6">
              <Field label="Email" type="email" testid="svc-email" value={form.email} onChange={h("email")} required />
              <Field label="Phone" type="tel" testid="svc-phone" value={form.phone} onChange={h("phone")} />
            </div>
            <Field label="Address" testid="svc-address" value={form.site_address} onChange={h("site_address")} />
            <Field label="Equipment / System" testid="svc-equipment" value={form.equipment} onChange={h("equipment")} placeholder="Chiller, RTU, boiler, VRF, split-system…" />

            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">
                Urgency
              </label>
              <div className="flex flex-wrap gap-2">
                {URGENCY.map((u) => (
                  <button
                    key={u}
                    type="button"
                    data-testid={`svc-urgency-${u.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => setForm((f) => ({ ...f, urgency: u }))}
                    className={`px-3 py-1.5 border font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-500 ${
                      form.urgency === u
                        ? "bg-white text-black border-white"
                        : "border-white/20 text-white/70 hover:border-white hover:text-white"
                    }`}
                  >
                    {u}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">
                Issue / Details *
              </label>
              <textarea
                value={form.message}
                onChange={h("message")}
                data-testid="svc-message"
                rows={5}
                placeholder="Describe the issue, symptoms, and any prior work…"
                className="w-full bg-transparent border-b border-white/20 focus:border-white outline-none py-3 text-lg text-white placeholder:text-white/25 transition-colors duration-500 resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="svc-submit"
              className="group inline-flex items-center gap-4 border border-white bg-white text-black px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] hover:bg-black hover:text-white transition-colors duration-500 disabled:opacity-50"
            >
              {loading ? "Sending…" : "Submit service request"}
              <span className="inline-block transition-transform duration-500 group-hover:translate-x-1">→</span>
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, placeholder }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">
        {label}{required ? " *" : ""}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        placeholder={placeholder}
        className="w-full bg-transparent border-b border-white/20 focus:border-white outline-none py-3 text-lg text-white placeholder:text-white/25 transition-colors duration-500"
      />
    </div>
  );
}
